import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ICON } from '@attendance/ui';
import { ChevronLeft } from 'lucide-react';
import { useFeatures } from '../../stores/brandingStore.js';
import { CompanyInfoSection } from './CompanyInfoPage.js';
import { BrandingSection } from './BrandingSettingsPage.js';
import { SectionShell } from './SettingsControls.js';
import { LETTER_TOKENS, MANAGED_ELSEWHERE } from './settingsCatalog.js';
import type { CompanyDraft } from './useCompanyDraft.js';

/* ===========================================================================
 * THE FOUR SMALL THINGS THE SETTINGS PAGE DRAWS AROUND ITS SECTIONS.
 *
 * Lifted out of `SettingsPage.tsx`, which is the CATALOG walk — which groups
 * exist, which are locked, which the search reaches. These four are what wraps
 * them on screen: a heading over a block of cards that is not one card, the
 * company identity line, the letter tokens, and the note that says a setting
 * lives on another page. Each keeps the argument for what it says.
 * ======================================================================== */

/**
 * A HEADING OVER A BLOCK OF CARDS THAT IS NOT ONE CARD — Branding, which is
 * four cards, so its name cannot live in a `CardHeader`. Company info owns its
 * own titled `Card` and is mounted bare.
 *
 * Not `SectionShell`: the children render their own `rounded-panel` cards, and
 * nesting one panel in another doubles the border. `text-h3` is the step
 * `SectionShell` and `CardTitle` both use, so the rank is the same either way.
 */
export function TitledSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="space-y-component">
      <h3 id={`${id}-title`} className="text-h3 text-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * `/settings/company` and `/settings/branding` used to be two routed pages,
 * each with its own PageShell chrome. They are two sections of this ONE group
 * now — `CompanyInfoSection` and `BrandingSection` render what those pages
 * rendered, minus the outer chrome this group already supplies.
 *
 * `CompanyInfoSection` is one titled `Card` and carries its own heading;
 * Branding is four cards, so `TitledSection` names it. Neither carries a Save:
 * the company record is part of this page's draft (`useCompanyDraft`) and goes
 * out with everything else from the bar at the foot of the page.
 *
 * WHAT MADE THIS READ AS PAGES STACKED, and what it does instead. The measure
 * (`max-w-form`) moved off the whole section and onto that section's FIELD GRID,
 * so every frame on this tab shares one right edge while the fields keep their
 * reading length; the spacing is `space-y-section` / `space-y-component` rather
 * than the off-scale `space-y-8`; and both topics are `<section
 * aria-labelledby>` landmarks rather than bare `<h3>`s over `<div>`s.
 */
export function CompanyIdentityBlock({ draft }: { draft: CompanyDraft }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-section">
      <CompanyInfoSection draft={draft} />
      <TitledSection
        id="settings-section-branding"
        title={t('settings.s.branding.title')}
      >
        <BrandingSection />
      </TitledSection>
    </div>
  );
}

export function LetterTokens(): JSX.Element {
  const { t } = useTranslation();
  return (
    <SectionShell
      id="settings-section-letter-tokens"
      title={t('settings.letters.tokensTitle')}
      blurb={t('settings.letters.tokensHint')}
    >
      <div className="flex flex-wrap gap-control">
        {LETTER_TOKENS.map((token) => (
          <code
            key={token}
            dir="ltr"
            className="rounded-xs bg-muted px-1.5 py-0.5 text-copy-sm text-foreground ring-1 ring-border"
          >
            {token}
          </code>
        ))}
      </div>
    </SectionShell>
  );
}

export function ManagedElsewhere(): JSX.Element {
  const { t } = useTranslation();
  const features = useFeatures();
  const links = MANAGED_ELSEWHERE.filter(
    (l) => !l.feature || features[l.feature] !== false
  );
  return (
    /* NO SECOND NAME FOR ONE THING (§5). This list used to sit in a
       `SectionShell` headed "Other screens", directly under the group heading
       — which was itself headed "Other modules", a third name, and one that
       collided with the "Modules" tab two along in the strip. The group is
       "Managed elsewhere" now, and the card frame and its heading are gone:
       every row here already draws its own bordered tile, so the frame was a
       box around boxes and the heading was the tab's name said twice. */
    <ul
      id="settings-section-elsewhere"
      /* AUTO-FIT, NOT A VIEWPORT LADDER (§4b). Layout only — no setting, no
         default and no write is touched here. */
      className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-element"
    >
      {links.map((link) => (
        <li key={link.id}>
          {/* `rounded-card` (12px) was the INNER-card role, correct while this
              row sat inside a `SectionShell`. With the shell gone the row is a
              top-level tile, which is `rounded-panel` — and it takes the rest
              of the tile recipe (`bg-surface`, the resting shadow) so it reads
              as an object on the page ground rather than an outline on it. */}
          <Link
            to={link.to}
            className="flex min-h-touch items-center justify-between gap-element rounded-panel border border-border bg-surface px-3 py-2 text-copy text-foreground shadow-resting transition-colors duration-fast ease-standard hover:bg-muted"
          >
            <span>
              <span className="block font-medium">
                {t(`settings.elsewhere.${link.id}.label`)}
              </span>
              <span className="block text-copy-sm text-muted-foreground">
                {t(`settings.elsewhere.${link.id}.hint`)}
              </span>
            </span>
            <ChevronLeft
              size={ICON.control}
              aria-hidden="true"
              className="shrink-0 rotate-180 text-muted-foreground rtl:rotate-0"
            />
          </Link>
        </li>
      ))}
    </ul>
  );
}
